import {
  Box,
  Link,
  Pagination,
  Skeleton,
  Theme,
  useMediaQuery,
} from "@mui/material";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import { pageFilters } from "models/interfaces";
import myPlanTokens from "screens/Settings/MyPlan/MyPlan.tokens";
import {
  columnHeaderSx,
  creditCellSx,
  emptyStateSx,
  invoiceCardIdSx,
  invoiceCardMetaSx,
  invoiceCardRowSx,
  invoiceCardTextSx,
  invoiceCardTopSx,
  invoiceDownloadButtonSx,
  listRowSx,
  paginationBarSx,
  paginationSx,
  pillSx,
  planCardSx,
  rowTextSx,
  sectionDescSx,
  sectionHeaderSx,
  sectionTitleSx,
} from "screens/Settings/MyPlan/MyPlan.styles";
import {
  formatInvoiceDate,
  getInvoiceStatusColors,
  invoiceEventLabel,
  InvoiceRow,
} from "screens/Settings/MyPlan/components/MyPlanHelper";
import { formatMoney } from "helpers/perSeatHelpers";
import { DownloadIcon } from "screens/Settings/MyPlan/components/PlanIcons";
import EmptyState from "global/components/EmptyState/EmptyState";

const t = myPlanTokens;

const BillingHistory = ({
  BillingDetails,
  totalCount,
  loading,
  billingFilters,
  pageChangeHandler,
}: {
  BillingDetails: InvoiceRow[];
  totalCount: number;
  loading: boolean;
  billingFilters: pageFilters;
  perPageRowsChangeHandler: (rowPerPage: number) => void;
  pageChangeHandler: (event: any, newPage: number) => void;
}) => {
  const canDownload = hasAccessTo(
    strings.resources.billing,
    strings.downloadPermission,
  );
  // Both small references replace the six-column grid with an invoice card.
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const rowsPerPage = billingFilters.rowsPerPage || 5;
  const pageCount = Math.ceil(totalCount / rowsPerPage);
  const columns = t.grid.invoiceRow;

  const getRow = (invoice: InvoiceRow, index: number) => {
    const status = getInvoiceStatusColors(invoice.paymentStatus);
    return (
      <Box
        key={`${invoice.invoiceNumber}-${index}`}
        sx={listRowSx(columns, index === BillingDetails.length - 1)}
      >
        <Box component="span" sx={rowTextSx(t.color.ink, t.weight.medium)}>
          {invoice.invoiceNumber}
        </Box>
        <Box component="span" sx={rowTextSx(t.color.body, t.weight.regular)}>
          {/* What the invoice was for — a renewal reads differently from a
              seat change, and the amounts only make sense with it. */}
          {invoiceEventLabel(invoice.eventType)
            ? `${invoice.planName} · ${invoiceEventLabel(invoice.eventType)}`
            : invoice.planName}
        </Box>
        <Box component="span" sx={rowTextSx(t.color.body, t.weight.regular)}>
          {formatInvoiceDate(invoice.startDate)}
        </Box>
        <Box component="span" sx={rowTextSx(t.color.body, t.weight.regular)}>
          {formatInvoiceDate(invoice.endDate)}
        </Box>
        {/* Credit hides the difference between what an invoice came to and
            what the card paid, so both are shown when they differ. */}
        <Box component="span" sx={rowTextSx(t.color.ink, t.weight.medium)}>
          {formatMoney(invoice.amountCharged, invoice.currency)}
          {invoice.creditApplied > 0 && (
            <Box component="span" sx={creditCellSx}>
              {`${formatMoney(
                invoice.gross,
                invoice.currency,
              )} less ${formatMoney(
                invoice.creditApplied,
                invoice.currency,
              )} credit`}
            </Box>
          )}
        </Box>

        <Box component="span">
          <Box
            component="span"
            sx={pillSx({
              color: status.color,
              bgcolor: status.bgcolor,
              fontSize: t.font.chip,
            })}
          >
            {(invoice.paymentStatus ?? "").toUpperCase()}
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {canDownload && invoice.invoiceUrl && (
            <Link
              href={invoice.invoiceUrl}
              target="_blank"
              rel="noopener"
              underline="none"
              aria-label={`Download invoice ${invoice.invoiceNumber}`}
              sx={{
                display: "flex",
                color: t.color.accent,
                "&:hover": { color: t.color.accentHover },
              }}
            >
              <DownloadIcon />
            </Link>
          )}
        </Box>
      </Box>
    );
  };

  const getCardRow = (invoice: InvoiceRow, index: number) => {
    const status = getInvoiceStatusColors(invoice.paymentStatus);
    return (
      <Box
        key={`${invoice.invoiceNumber}-${index}`}
        sx={invoiceCardRowSx(index === BillingDetails.length - 1)}
      >
        <Box sx={invoiceCardTextSx}>
          <Box sx={invoiceCardTopSx}>
            <Box component="span" sx={invoiceCardIdSx}>
              {invoice.invoiceNumber}
            </Box>
            <Box
              component="span"
              sx={pillSx({
                color: status.color,
                bgcolor: status.bgcolor,
                fontSize: t.font.chip,
              })}
            >
              {(invoice.paymentStatus ?? "").toUpperCase()}
            </Box>
          </Box>
          <Box component="span" sx={invoiceCardMetaSx}>
            {`${invoice.planName} \u00b7 ${formatInvoiceDate(
              invoice.startDate,
            )} \u2013 ${formatInvoiceDate(invoice.endDate)}`}
          </Box>
        </Box>
        {canDownload && invoice.invoiceUrl && (
          <Link
            href={invoice.invoiceUrl}
            target="_blank"
            rel="noopener"
            underline="none"
            aria-label={`Download invoice ${invoice.invoiceNumber}`}
            sx={invoiceDownloadButtonSx}
          >
            <DownloadIcon />
          </Link>
        )}
      </Box>
    );
  };

  const getLoadingRows = () =>
    [...Array(rowsPerPage)].map((_, index) => (
      <Box key={index} sx={listRowSx(columns, index === rowsPerPage - 1)}>
        {[...Array(7)].map((__, cell) => (
          <Skeleton key={cell} variant="text" width="70%" height={20} />
        ))}
      </Box>
    ));

  return (
    <Box sx={planCardSx}>
      <Box sx={sectionHeaderSx(true)}>
        <Box component="h2" sx={sectionTitleSx}>
          Invoices
        </Box>
        <Box sx={sectionDescSx}>Download receipts for your records.</Box>
      </Box>

      <Box sx={columnHeaderSx(columns)}>
        <Box component="span">INVOICE</Box>
        <Box component="span">PLAN</Box>
        <Box component="span">START</Box>
        <Box component="span">END</Box>
        <Box component="span">CHARGED</Box>
        <Box component="span">STATUS</Box>
        <Box component="span" sx={{ textAlign: "right" }}>
          ACTION
        </Box>
      </Box>

      {loading ? (
        getLoadingRows()
      ) : BillingDetails.length > 0 ? (
        BillingDetails.map(isCompact ? getCardRow : getRow)
      ) : (
        /* Read-only: invoices arrive from billing, so the block carries no action. */
        <EmptyState
          icon="invoice"
          accent="amber"
          title="No invoices yet"
          body="Your first invoice appears here once the first billing cycle closes."
        />
      )}

      {!loading && pageCount > 1 && (
        <Box sx={paginationBarSx}>
          <Pagination
            page={billingFilters.page ?? 1}
            count={pageCount}
            siblingCount={1}
            boundaryCount={1}
            shape="rounded"
            onChange={(event, page: number) => pageChangeHandler(event, page)}
            sx={paginationSx}
          />
        </Box>
      )}
    </Box>
  );
};

export default BillingHistory;
